import React from 'react';

interface DesktopLayoutProps {
  /** Content for the left side (question and answers) */
  leftContent: React.ReactNode;
  /** Content for the right side (standings) */
  rightContent: React.ReactNode;
  /** Optional mobile-only content (shown at top on mobile) */
  mobileOnlyContent?: React.ReactNode;
  /** Grid gap (default: '2rem') */
  gap?: string;
  /** Right sidebar width (default: '320px' for desktop, '350px' for larger screens) */
  rightWidth?: string;
  /** Maximum width of the grid container (default: '1400px') */
  maxWidth?: string;
  /** Additional className for the grid container */
  className?: string;
}

/**
 * Responsive layout component that displays:
 * - Desktop/Big screens: Left (question/answers) | Right (standings)
 * - Mobile/Tablet: Stacked vertically with mobile-only content at top
 * 
 * Uses CSS Grid for desktop and flexbox for mobile
 */
export const DesktopLayout: React.FC<DesktopLayoutProps> = ({
  leftContent,
  rightContent,
  mobileOnlyContent,
  gap = '2rem',
  rightWidth = '320px',
  maxWidth = '100%',
  className = '',
}) => {
  return (
    <>
      {/* Mobile-only content (shown at top on smaller screens) */}
      {mobileOnlyContent && (
        <div className="mobile-only" style={{ marginBottom: '1rem' }}>
          {mobileOnlyContent}
        </div>
      )}

      <div 
        className={`desktop-layout-grid ${className}`}
        style={{
          display: 'grid',
          gridTemplateColumns: `1fr ${rightWidth}`,
          gap,
          height: '100%',
          minHeight: 0,
          maxWidth,
          margin: '0 auto',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        {/* Main content - left side */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {leftContent}
        </div>

        {/* Right sidebar - Desktop only */}
        <div className="desktop-only" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {rightContent}
        </div>
      </div>

      <style>{`
        .mobile-only { display: none; }
        .desktop-only { display: block; min-height: 0; }
        
        /* All desktop sizes: keep a fixed right sidebar width */
        @media (min-width: 1025px) {
          .desktop-layout-grid {
            grid-template-columns: 1fr ${rightWidth} !important;
          }
        }
        
        @media (max-width: 1024px) {
          .mobile-only { display: block; }
          .desktop-only { display: none !important; }
          .desktop-layout-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </>
  );
};
